import GenerativeIndicatorIcon from '@atlaskit/icon-lab/core/generative-indicator';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { Pressable, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { getCachedViewSnapshot } from '@databases/database-state/state/view';
import { useFlags } from '@databases/flags';
import { messages } from '@databases/intl/messages';
import {
  aiFieldConfigValidator,
  type AiSummaryFieldConfig,
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue,
  isDatabaseTextValue
} from '@databases/types/database';
import React, {
  type FunctionComponent,
  type ReactElement,
  useCallback,
  useMemo,
  useRef
} from 'react';
import { useIntl } from 'react-intl-next';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { formatRelativeTime } from '@canvas/intl/format-relative-time';

import {
  aiFieldCellRequestCoordinator,
  useIsAiFieldCellProcessing
} from '../cell-request-coordinator/index.ts';

import { runManualAiSummaryRefresh } from './run-ai-summary-field.ts';
import { useIsAiSummaryFieldEnabled } from './use-is-ai-summary-field-enabled.ts';

const buttonStyles = xcss({
  width: '24px',
  height: '24px',
  padding: 'space.0',
  borderRadius: 'radius.medium',
  backgroundColor: 'elevation.surface.overlay',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'box-shadow 0.2s, background-color 0.2s',
  ':hover': {
    cursor: 'pointer',
    backgroundColor: 'color.background.neutral.hovered',
    boxShadow: 'elevation.shadow.overlay'
  }
});

export interface UseAiSummaryCellActionOptions {
  contentId: string;
  entryId: string;
  field: DatabaseField;
  isVisible: boolean;
  value: DatabaseValue;
}

export type UseAiSummaryCellActionResult =
  | {
      isAiSummaryFieldEnabled: false;
      cellAction: undefined;
    }
  | {
      isAiSummaryFieldEnabled: true;
      cellAction: ReactElement | undefined;
    };

const AI_SUMMARY_DISABLED_RESULT: UseAiSummaryCellActionResult = {
  isAiSummaryFieldEnabled: false,
  cellAction: undefined
};
const AI_SUMMARY_ENABLED_WITHOUT_ACTION_RESULT: UseAiSummaryCellActionResult = {
  isAiSummaryFieldEnabled: true,
  cellAction: undefined
};
const DEFAULT_AI_SUMMARY_FIELD_CONFIG = aiFieldConfigValidator.parse({
  aiFieldType: 'summary'
}).field;

const AI_SUMMARY_CELL_RUN_ERROR_FLAG_ID = 'ai-summary-cell-run-error';

interface AiSummaryCellActionProps {
  contentId: string;
  entryId: string;
  fieldId: string;
  targetFieldName: string;
  config: AiSummaryFieldConfig;
  value: DatabaseValue;
}

const AiSummaryCellAction: FunctionComponent<AiSummaryCellActionProps> = ({
  contentId,
  entryId,
  fieldId,
  targetFieldName,
  config,
  value
}) => {
  const intl = useIntl();
  const { addFlag, clearFlags, hideFlag, showFlag } = useFlags();
  const activeErrorFlagRef = useRef<{
    id: string;
    usesBridgeFlags: boolean;
  } | null>(null);
  const lastUpdatedAt =
    value.type === DatabaseFieldType.AI_SUMMARY
      ? value.value.metadata?.lastUpdatedAt
      : undefined;
  const valueState =
    isDatabaseTextValue(value.value) && value.value.plain.trim().length > 0
      ? 'existing'
      : 'empty';
  const target = useMemo(
    () => ({
      contentId,
      aiFieldType: 'summary' as const,
      fieldId,
      entryId,
      isTargetValueEmpty: valueState === 'empty'
    }),
    [contentId, entryId, fieldId, valueState]
  );
  const isCellProcessing = useIsAiFieldCellProcessing(target);
  const enqueueRequest = useCallback(() => {
    const viewId = getCachedViewSnapshot().id;

    return aiFieldCellRequestCoordinator.enqueue({
      target,
      batchKey: JSON.stringify([
        contentId,
        viewId,
        'summary',
        fieldId,
        targetFieldName,
        config.outputFormat,
        intl.locale
      ]),
      run: async (targets) => {
        await runManualAiSummaryRefresh({
          contentId,
          fieldId,
          targetFieldName,
          config,
          locale: intl.locale,
          viewId,
          triggerType: 'singleEntry',
          runScope: 'singleEntry',
          source: 'databaseView',
          ...(targets.length === 1
            ? {
                operation: 'single-entry-generate' as const,
                entryId: targets[0].entryId
              }
            : {
                operation: 'multiple-entry-generate' as const,
                entryIds: targets.map(({ entryId }) => entryId)
              })
        });
      }
    });
  }, [config, contentId, fieldId, intl.locale, target, targetFieldName]);

  const dismissErrorFlag = useCallback(() => {
    const activeErrorFlag = activeErrorFlagRef.current;
    if (!activeErrorFlag) {
      return;
    }

    activeErrorFlagRef.current = null;
    if (activeErrorFlag.usesBridgeFlags) {
      hideFlag(activeErrorFlag.id);
    } else {
      clearFlags();
    }
  }, [clearFlags, hideFlag]);

  const showErrorFlag = useCallback(() => {
    if (activeErrorFlagRef.current) {
      return;
    }

    const flagId = `${AI_SUMMARY_CELL_RUN_ERROR_FLAG_ID}:${contentId}:${fieldId}:${entryId}:${Date.now()}`;
    const usesBridgeFlags = isFeatureFlagEnabled('database-frontend-flags-over-bridge');
    activeErrorFlagRef.current = { id: flagId, usesBridgeFlags };

    const clearErrorFlagState = () => {
      if (activeErrorFlagRef.current?.id === flagId) {
        activeErrorFlagRef.current = null;
      }
    };
    const tryAgain = () => {
      const result = enqueueRequest();
      if (result.kind === 'enqueued') {
        void result.completion.then(dismissErrorFlag).catch(() => undefined);
      }
    };
    const flag = {
      title: intl.formatMessage(
        messages['field-actions.ai-summary-cell-run-error-title']
      ),
      description: intl.formatMessage(
        messages['field-actions.ai-summary-cell-run-error-description']
      ),
      actions: [
        {
          content: intl.formatMessage(
            messages['field-actions.ai-summary-cell-run-error-try-again']
          ),
          onClick: tryAgain
        }
      ]
    };

    if (usesBridgeFlags) {
      showFlag({
        ...flag,
        id: flagId,
        type: 'error',
        appearance: 'normal',
        close: 'auto',
        onClose: clearErrorFlagState
      });
    } else {
      addFlag({
        ...flag,
        appearance: 'normal',
        onClose: clearErrorFlagState,
        icon: () => (
          <StatusErrorIcon
            color={token('color.icon.danger', '#C9372C')}
            label=""
            spacing="spacious"
          />
        )
      });
    }
  }, [
    addFlag,
    contentId,
    dismissErrorFlag,
    enqueueRequest,
    entryId,
    fieldId,
    intl,
    showFlag
  ]);
  const handleClick = () => {
    const result = enqueueRequest();

    if (result.kind === 'enqueued') {
      void result.completion.catch(showErrorFlag);
    }
  };

  const actionTooltip = intl.formatMessage(
    valueState === 'empty'
      ? messages['field-actions.ai-summary-cell-generate']
      : messages['field-actions.ai-summary-cell-regenerate']
  );
  const relativeTime = formatRelativeTime(lastUpdatedAt, intl);
  const tooltipContent = relativeTime
    ? intl.formatMessage(messages['field-actions.ai-summary-cell-last-updated'], {
        action: actionTooltip,
        relativeTime
      })
    : actionTooltip;

  return (
    <Tooltip position="top" content={tooltipContent}>
      <Pressable
        aria-label={actionTooltip}
        isDisabled={isCellProcessing}
        onClick={(event) => {
          event.stopPropagation();
          handleClick();
        }}
        xcss={buttonStyles}
        testId="cell-ai-summary-generate-button"
      >
        <GenerativeIndicatorIcon label="" size="small" color={token('color.icon')} />
      </Pressable>
    </Tooltip>
  );
};

export const useAiSummaryCellAction = ({
  contentId,
  entryId,
  field,
  isVisible,
  value
}: UseAiSummaryCellActionOptions): UseAiSummaryCellActionResult => {
  const isAiSummaryFieldEnabled = useIsAiSummaryFieldEnabled();

  if (!isAiSummaryFieldEnabled) {
    return AI_SUMMARY_DISABLED_RESULT;
  }

  if (!isVisible || field.type !== DatabaseFieldType.AI_SUMMARY) {
    return AI_SUMMARY_ENABLED_WITHOUT_ACTION_RESULT;
  }

  const config =
    field.settings.aiFieldConfig?.aiFieldType === 'summary'
      ? field.settings.aiFieldConfig.field
      : DEFAULT_AI_SUMMARY_FIELD_CONFIG;

  return {
    isAiSummaryFieldEnabled: true,
    cellAction: (
      <AiSummaryCellAction
        contentId={contentId}
        entryId={entryId}
        fieldId={field.id}
        targetFieldName={field.name}
        config={config}
        value={value}
      />
    )
  };
};
